// core react redux
import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// VP UI
import {
  FormField,
  FormLabel,
  Typography,
  FlexBox,
  TextInput,
  Button,
  Spinner,
  Icon,
  VisuallyHidden,
  TextInputWithButtonInset,
} from '@vp/swan';

// state
import { useTranslations } from '@vp/om-translations';
import { pricingOperations, pricingSelectors } from '../../state/pricing';
import useIdentityContext from '../../hooks/useIdentityContext';

function CouponEntry({
  coupon,
  setCoupon,
  clearCoupon,
  couponChangeInProgress,
  setEntryFormShown,
}) {
  const [couponMessages, cartMessages] = useTranslations([
    'couponMessages',
    'cartMessages',
  ]);
  const [couponHasChanged, setCouponHasChanged] = useState(false);
  const [newCouponCode, setNewCouponCode] = useState('');

  const { identity } = useIdentityContext();
  const { isAnonymousUser } = identity;

  let couponValues = { defaultValue: coupon || '' };

  if (coupon && !couponHasChanged) {
    couponValues = { value: coupon || '', defaultValue: coupon || '' };
  } else if (coupon && couponHasChanged && newCouponCode === '') {
    couponValues = { value: '', defaultValue: '' };
  } else if (!coupon) {
    if (!couponHasChanged) {
      couponValues = { value: '', defaultValue: '' };
    }
  }

  const onChange = event => {
    const value = event.target.value.trim();
    if (value !== coupon) {
      setCouponHasChanged(true);
      setNewCouponCode(value);
    } else {
      setCouponHasChanged(false);
    }
  };

  const onClearCoupon = () => {
    clearCoupon();
    setEntryFormShown(false);
  };

  const onCouponSubmit = () => {
    if (newCouponCode === '') {
      onClearCoupon();
    } else if (couponHasChanged === true) {
      setCoupon(newCouponCode, isAnonymousUser);
      setEntryFormShown(false);
    }
    setCouponHasChanged(false);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      onCouponSubmit();
    }
  };

  if (couponChangeInProgress) {
    return (
      <Spinner
        accessibleText={cartMessages.loading}
        aria-busy="true"
        aria-live="polite"
        role="status"
      />
    );
  }

  return (
    <>
      {/* Temporarily use the class-based API until React Library supports this */}
      <FormField>
        <VisuallyHidden>
          <FormLabel>{couponMessages.enterPromoCode}</FormLabel>
        </VisuallyHidden>
        <FlexBox>
          <TextInputWithButtonInset style={{ flexGrow: 1 }}>
            <TextInput
              mr={2}
              size="mini"
              onChange={onChange}
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...couponValues}
              onKeyPress={handleKeyPress}
              bgc="white"
            />
            {coupon && (
              <Button onClick={onClearCoupon}>
                <Icon iconType="close" mr={2} />
                <Typography>
                  <VisuallyHidden>{couponMessages.clear}</VisuallyHidden>
                </Typography>
              </Button>
            )}
          </TextInputWithButtonInset>
          <Button
            skin="secondary"
            onClick={onCouponSubmit}
            disabled={!couponHasChanged}
            ml={2}
            size="mini"
          >
            {couponMessages.apply}
          </Button>
        </FlexBox>
      </FormField>
    </>
  );
}

CouponEntry.propTypes = {
  coupon: PropTypes.any,
  setCoupon: PropTypes.func.isRequired,
  clearCoupon: PropTypes.func.isRequired,
  couponChangeInProgress: PropTypes.bool,
  couponError: PropTypes.string,
  setEntryFormShown: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  setCoupon: (couponCode, isAnonymousUser) =>
    dispatch(pricingOperations.setCoupon(couponCode, isAnonymousUser)),
  clearCoupon: () => dispatch(pricingOperations.clearCoupon()),
});

const mapStateToProps = state => {
  const props = {
    coupon: pricingSelectors.selectDiscountCodeName(state.pricing),
    couponChangeInProgress: pricingSelectors.selectCouponChangeInProgress(
      state.pricing
    ),
    couponError: pricingSelectors.selectCouponErrorMessageKey(state.pricing),
  };
  return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(CouponEntry);
